import { Mail, Database, Code } from 'lucide-react';
import { LATEST_BLOG_POSTS } from './latestBlogPosts';

export const PROBLEMS = [
  {
    icon: Mail,
    title: "Email Forms Are Unreliable",
    description: "mailto: links don't work on mobile, get caught in spam filters, and provide no tracking or analytics."
  },
  {
    icon: Database,
    title: "Backend Setup Takes Forever",
    description: "Setting up servers, databases, and APIs for simple contact forms wastes hours of development time."
  },
  {
    icon: Code,
    title: "Complex Integration Headaches",
    description: "Third-party form services require complex JavaScript, break your design, and lock you into their ecosystem."
  }
];

export const HOW_IT_WORKS_STEPS = [
  {
    step: 1,
    title: 'Name the form',
    description: 'Copy the URL. Email goes to your account address.',
  },
  {
    step: 2,
    title: 'Paste it into action',
    description: 'Keep your fields, styling, and page.',
  },
  {
    step: 3,
    title: 'Open the inbox',
    description: 'The email lists every text field.',
  },
];

export const AUDIENCES = [
  {
    label: 'Freelancers & agencies',
    title: 'Ship client forms without backend work',
    description:
      'You finish the site. The client needs a contact form. You do not want to host servers or fix email delivery later.',
    need: 'A clean handoff the client can run themselves',
    wins: [
      'Paste one endpoint into existing HTML',
      'No server upkeep after launch',
      'CSV export for reporting and closeout',
    ],
    highlight: 'Built for client projects',
  },
  {
    label: 'Frontend developers',
    title: 'Keep full control of the UI',
    description:
      'You own the interface and markup. FormCare handles submissions, files, and notifications behind one URL.',
    need: 'Form handling without writing backend code',
    wins: [
      'Works with any framework or static site',
      'Keep your HTML, CSS, and components',
      'Dashboard for every submission and file',
    ],
    highlight: 'Build faster, deploy sooner',
  },
  {
    label: 'Startups & small teams',
    title: 'Launch forms before the backend is ready',
    description:
      'Landing pages, waitlists, and beta signups should go live now — not after infrastructure is built.',
    need: 'Reliable forms without hiring backend help',
    wins: [
      'Go live in minutes, not weeks',
      'Collect leads in one place',
      'Redirects and alerts out of the box',
    ],
    highlight: 'Ship features, not infrastructure',
  },
];

export const FEATURES = [
  {
    title: "Files up to 10 MB",
    description: "Up to 5 files per submission."
  },
  {
    title: "Email on every accepted submission",
    description: "The message lists every text field."
  },
  {
    title: "Spam filtered before it is stored",
    description: "Rejected spam is not emailed."
  },
  {
    title: "CSV export and a thank-you redirect",
    description: "Download submissions or send people to your own page."
  }
];

export const WORKFLOWS = [
  {
    label: 'Team routing',
    title: 'Send each form to the right person',
    problem: 'Sales, support, and partnerships all land in one inbox. Replies get lost or answered too late.',
    steps: [
      'Create one endpoint per form',
      'Choose who gets notified for each',
      'Review every reply in one dashboard',
    ],
    result: 'Each team sees only what belongs to them.',
  },
  {
    label: 'Client handoff',
    title: 'Ship a contact form without backend work',
    problem: 'The site is done, but the client still needs a form — and you do not want to maintain servers.',
    steps: [
      'Keep the client HTML as-is',
      'Paste one FormCare endpoint URL',
      'Hand over the dashboard, not server upkeep',
    ],
    result: 'The form works on day one with no extra hosting or email setup.',
  },
  {
    label: 'Launch validation',
    title: 'Collect signups on a landing page today',
    problem: 'The page is ready, but backend work would slow down the launch.',
    steps: [
      'Create a signup endpoint',
      'Add a redirect or email alert',
      'Export responses when the test ends',
    ],
    result: 'You start learning from real signups before building a custom backend.',
  },
];

// Use the optimized latest blog posts data for better performance
export const BLOG_POSTS = LATEST_BLOG_POSTS;

export const SEO_STRUCTURED_DATA = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "FormCare",
  "description": "Paste one URL into an HTML form. FormCare stores submissions and files and emails you. 150 free submissions a month and 10 forms, no credit card.",
  "url": "https://formcare.io",
  "applicationCategory": "DeveloperApplication",
  "operatingSystem": "Web",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "EUR",
    "description": "150 free submissions per month and 10 forms. Extra submissions are 2 cents each in EUR."
  },
  "creator": {
    "@type": "Organization",
    "name": "FormCare",
    "url": "https://formcare.io"
  },
  "featureList": [
    "HTML form backend endpoint",
    "File upload handling",
    "Form submission dashboard",
    "No backend coding required",
    "Works with any tech stack",
    "Custom Notification Emails",
    "Secure file storage",
    "Built-in anti-spam (honeypot, name checks, rate limits)",
    "CSV export"
  ]
}; 